import React, { useMemo } from 'react';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Animated starfield background.
 * - Twinkling stars (scale + opacity) for ambient depth
 * - Blinking stars (hard on/off) sprinkled randomly with long pauses
 * - 2 satellites drifting slowly across the sky (white/blue, not green)
 * Pure CSS animations. Only renders in dark mode.
 */
export const Starfield = ({ force = false }) => {
  const { isDark } = useTheme();

  // Generate star positions deterministically per page load
  const { twinklers, blinkers } = useMemo(() => {
    const seedRand = (i) => {
      const x = Math.sin(i * 9301 + 49297) * 233280;
      return x - Math.floor(x);
    };
    const twinklers = [];
    for (let i = 0; i < 120; i++) {
      const size = seedRand(i) < 0.85 ? 'sm' : seedRand(i + 500) < 0.7 ? 'md' : 'lg';
      twinklers.push({
        id: i,
        top: seedRand(i + 100) * 100,
        left: seedRand(i + 200) * 100,
        delay: seedRand(i + 300) * 8,
        duration: 2.5 + seedRand(i + 400) * 4,
        size,
      });
    }
    // Blinkers: ~25 stars that just turn on/off with long pauses between blinks
    const blinkers = [];
    for (let i = 0; i < 25; i++) {
      blinkers.push({
        id: i + 1000,
        top: seedRand(i + 700) * 100,
        left: seedRand(i + 800) * 100,
        // Long cycle so the page doesn't feel busy
        duration: 6 + seedRand(i + 900) * 8, // 6-14s
        delay: seedRand(i + 950) * 12,       // staggered 0-12s
      });
    }
    return { twinklers, blinkers };
  }, []);

  if (!isDark && !force) return null;

  return (
    <div className="plancity-starfield" aria-hidden="true">
      {/* Twinkling stars (smooth scale + opacity loop) */}
      {twinklers.map((s) => (
        <span
          key={s.id}
          className={`pc-star pc-star-${s.size}`}
          style={{
            top: `${s.top}%`,
            left: `${s.left}%`,
            animationDelay: `${s.delay}s`,
            animationDuration: `${s.duration}s`,
          }}
        />
      ))}

      {/* Blinking stars (hard on/off with pauses) */}
      {blinkers.map((s) => (
        <span
          key={s.id}
          className="pc-blink"
          style={{
            top: `${s.top}%`,
            left: `${s.left}%`,
            animationDelay: `${s.delay}s`,
            animationDuration: `${s.duration}s`,
          }}
        />
      ))}

      {/* Slow satellites (white/blue) drifting across */}
      <span className="pc-satellite pc-satellite-1" />
      <span className="pc-satellite pc-satellite-2" />

      <style>{`
        .plancity-starfield {
          position: fixed;
          inset: 0;
          pointer-events: none;
          overflow: hidden;
          z-index: 0;
        }

        /* ----- Twinkling stars ----- */
        .pc-star {
          position: absolute;
          background: #fff;
          border-radius: 9999px;
          opacity: 0;
          animation: pc-twinkle ease-in-out infinite;
        }
        .pc-star-sm { width: 1px; height: 1px; box-shadow: 0 0 2px rgba(255,255,255,0.6); }
        .pc-star-md { width: 2px; height: 2px; box-shadow: 0 0 4px rgba(255,255,255,0.75); }
        .pc-star-lg {
          width: 3px;
          height: 3px;
          box-shadow: 0 0 6px rgba(255,255,255,0.85), 0 0 12px rgba(180, 220, 255, 0.45);
        }
        @keyframes pc-twinkle {
          0%, 100% { opacity: 0.15; transform: scale(0.85); }
          50% { opacity: 0.95; transform: scale(1.1); }
        }

        /* ----- Blinking stars (hard on/off with long pauses) ----- */
        .pc-blink {
          position: absolute;
          width: 2px;
          height: 2px;
          background: #fff;
          border-radius: 9999px;
          box-shadow: 0 0 4px rgba(255,255,255,0.85);
          opacity: 0;
          animation: pc-blink steps(1, end) infinite;
        }
        @keyframes pc-blink {
          0%, 6% { opacity: 1; }
          7%, 100% { opacity: 0; }
        }

        /* ----- Slow satellites (white-blue) ----- */
        .pc-satellite {
          position: absolute;
          width: 3px;
          height: 3px;
          border-radius: 9999px;
          background: #e0f0ff;
          box-shadow:
            0 0 6px rgba(220, 235, 255, 0.85),
            0 0 14px rgba(96, 165, 250, 0.55);
          opacity: 0;
        }
        .pc-satellite-1 {
          top: 18%; left: -2%;
          animation: pc-drift-1 70s linear 8s infinite;
        }
        .pc-satellite-2 {
          top: 70%; left: -2%;
          animation: pc-drift-2 95s linear 45s infinite;
        }
        @keyframes pc-drift-1 {
          0% { opacity: 0; transform: translateX(0); }
          5% { opacity: 0.9; }
          95% { opacity: 0.9; }
          100% { opacity: 0; transform: translateX(110vw); }
        }
        @keyframes pc-drift-2 {
          0% { opacity: 0; transform: translateX(0) translateY(0); }
          5% { opacity: 0.8; }
          95% { opacity: 0.8; transform: translateX(110vw) translateY(-40px); }
          100% { opacity: 0; transform: translateX(110vw) translateY(-40px); }
        }

        /* Respect user preference */
        @media (prefers-reduced-motion: reduce) {
          .pc-star, .pc-blink, .pc-satellite { animation: none !important; opacity: 0.4; }
        }
      `}</style>
    </div>
  );
};

export default Starfield;
